"use client";

import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import Link from "next/link";
import CommonHero from "@/components/CommonHero";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";

const cookieData = [
  {
    name: "session_id",
    provider: "AWS",
    purpose: "Maintains user session state across page requests.",
    expiry: "Session",
    type: "Essential"
  },
  {
    name: "_ga",
    provider: "Google Analytics",
    purpose: "Used to distinguish users for website analytics.",
    expiry: "2 years",
    type: "Analytics"
  },
  {
    name: "_gid",
    provider: "Google Analytics",
    purpose: "Used to distinguish users for website analytics.",
    expiry: "24 hours",
    type: "Analytics"
  },
  {
    name: "cookie_consent",
    provider: "AWS",
    purpose: "Stores the user's cookie consent preference.",
    expiry: "1 year",
    type: "Essential"
  },
  {
    name: "fbp",
    provider: "Facebook",
    purpose: "Used by Facebook to deliver advertisement products.",
    expiry: "3 months",
    type: "Marketing"
  }
];

export default function CookiesPage() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <main className="bg-white">
        <section className="bg-[#fafafa] border-b border-gray-100 pt-24">
          <div className="max-w-4xl mx-auto px-6">
            <div className="flex items-center gap-4 mb-6">
              <Link
                href="/"
                className="flex items-center gap-2 text-[#B8893A] hover:text-[#A67B34] transition-colors"
              >
                <ArrowLeft size={20} />
                <span className="font-medium">Back to Home</span>
              </Link>
            </div>
          </div>
        </section>

        <CommonHero
          badge="Privacy"
          title="Cookie"
          highlight="Policy"
          description="Transparent information about the cookies we use to improve your experience."
        />

        <section className="py-16 sm:py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-[#0A0A0A] mb-4">What are cookies?</h2>
              <p className="text-gray-600 leading-relaxed text-lg">
                Cookies are small text files that are placed on your computer by websites that you visit. They are widely used in order to make websites work, or work more efficiently, as well as to provide information to the owners of the site.
              </p>
            </div>

            <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-bottom border-gray-200">
                    <th className="px-6 py-4 font-bold text-[#0A0A0A] text-sm uppercase tracking-wider">Cookie Name</th>
                    <th className="px-6 py-4 font-bold text-[#0A0A0A] text-sm uppercase tracking-wider">Provider</th>
                    <th className="px-6 py-4 font-bold text-[#0A0A0A] text-sm uppercase tracking-wider">Purpose</th>
                    <th className="px-6 py-4 font-bold text-[#0A0A0A] text-sm uppercase tracking-wider">Expiry</th>
                    <th className="px-6 py-4 font-bold text-[#0A0A0A] text-sm uppercase tracking-wider">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cookieData.map((cookie, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-5 font-mono text-xs text-[#B8893A]">{cookie.name}</td>
                      <td className="px-6 py-5 text-gray-700 text-sm">{cookie.provider}</td>
                      <td className="px-6 py-5 text-gray-600 text-sm leading-relaxed">{cookie.purpose}</td>
                      <td className="px-6 py-5 text-gray-500 text-sm">{cookie.expiry}</td>
                      <td className="px-6 py-5">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          cookie.type === 'Essential' ? 'bg-green-100 text-green-700' :
                          cookie.type === 'Analytics' ? 'bg-blue-100 text-blue-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {cookie.type}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-16 prose prose-gray max-w-none">
              <h2 className="text-2xl font-bold text-[#0A0A0A] mb-4">How to manage cookies?</h2>
              <p className="text-gray-600 leading-relaxed text-lg mb-6">
                Most web browsers allow some control of most cookies through the browser settings. To find out more about cookies, including how to see what cookies have been set, visit <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-[#B8893A] underline">www.aboutcookies.org</a> or <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-[#B8893A] underline">www.allaboutcookies.org</a>.
              </p>
              <p className="text-gray-600 leading-relaxed text-lg">
                To opt out of being tracked by Google Analytics across all websites, visit <a href="http://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-[#B8893A] underline">http://tools.google.com/dlpage/gaoptout</a>.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
